import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function useModalFocusTrap({
  open,
  containerRef,
  trapFocus = true,
  returnFocusOnClose = true,
}: {
  open: boolean;
  containerRef: RefObject<HTMLElement | null>;
  trapFocus?: boolean;
  returnFocusOnClose?: boolean;
}): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    previousFocusRef.current = returnFocusOnClose && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const container = containerRef.current;
    if (trapFocus) {
      const firstFocusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? container;
      firstFocusable?.focus();
    }

    const handleTab = (event: KeyboardEvent) => {
      if (!trapFocus || event.key !== 'Tab') return;
      const node = containerRef.current;
      if (!node) return;
      // A Glaze popover opened from inside the dialog is portaled to <body>, outside `node`, but it owns
      // its own focus order (and returns focus to its trigger at the edges): don't pull focus out of it.
      const active = document.activeElement;
      if (active instanceof Element && !node.contains(active) && active.closest('[data-slot="popover-panel"]')) return;

      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((element) => !element.hasAttribute('disabled'));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !node.contains(current))) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && current === last) {
        first.focus();
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [containerRef, open, returnFocusOnClose, trapFocus]);
}
